import { useEffect, useState } from "react";
import { partenairesApi } from "@/lib/api";
import { toast } from "sonner";
import {
  PageHeader,
  Card,
  Btn,
  Field,
  EmptyState,
} from "@/components/DashboardUI";
import { Store, Plus, X, Trash2 } from "lucide-react";

const DEFAULT = {
  nom: "",
  categorie: "Vin",
  offre: "",
  valeurMaxEur: 5,
  emoji: "🍷",
  adresse: "",
  lat: null,
  lng: null,
  plan: "GRATUIT",
  actif: true,
};

export default function Partenaires() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(DEFAULT);
  const [show, setShow] = useState(false);

  const reload = () =>
    partenairesApi
      .list()
      .then(setItems)
      .catch(() => toast.error("Erreur"));
  useEffect(() => {
    reload();
  }, []);

  async function create(e) {
    e.preventDefault();
    try {
      await partenairesApi.create(form);
      toast.success("Partenaire créé");
      setShow(false);
      setForm(DEFAULT);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  }

  async function remove(id) {
    if (!window.confirm("Supprimer ?")) return;
    try {
      await partenairesApi.remove(id);
      toast.success("Supprimé");
      reload();
    } catch {
      toast.error("Erreur");
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="partenaires-page">
      <PageHeader
        chapter="Chapitre pro · Le terroir"
        title="Partenaires"
        subtitle={`${items.length} producteurs référencés`}
        testId="part-header"
        actions={
          <Btn
            variant={show ? "secondary" : "primary"}
            onClick={() => setShow(!show)}
            data-testid="btn-nouveau-partenaire"
          >
            {show ? (
              <>
                <X className="w-4 h-4" />
                Annuler
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Ajouter
              </>
            )}
          </Btn>
        }
      />

      {show && (
        <form onSubmit={create} data-testid="partenaire-form">
          <Card>
            <h2 className="font-display text-xl text-parchemin mb-6">
              Nouveau partenaire
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Nom"
                value={form.nom}
                onChange={(v) => setForm({ ...form, nom: v })}
                required
                testid="form-part-nom"
              />
              <Field
                label="Catégorie"
                value={form.categorie}
                onChange={(v) => setForm({ ...form, categorie: v })}
                testid="form-part-cat"
              />
              <Field
                label="Offre"
                value={form.offre}
                onChange={(v) => setForm({ ...form, offre: v })}
                required
                testid="form-part-offre"
              />
              <Field
                label="Valeur max (€)"
                type="number"
                value={form.valeurMaxEur}
                onChange={(v) =>
                  setForm({ ...form, valeurMaxEur: parseFloat(v) })
                }
                testid="form-part-valeur"
              />
              <Field
                label="Emoji"
                value={form.emoji}
                onChange={(v) => setForm({ ...form, emoji: v })}
                testid="form-part-emoji"
              />
              <Field
                label="Adresse"
                value={form.adresse}
                onChange={(v) => setForm({ ...form, adresse: v })}
                testid="form-part-adresse"
              />
              <Field
                label="Latitude"
                type="number"
                value={form.lat || ""}
                onChange={(v) =>
                  setForm({ ...form, lat: v ? parseFloat(v) : null })
                }
                testid="form-part-lat"
              />
              <Field
                label="Longitude"
                type="number"
                value={form.lng || ""}
                onChange={(v) =>
                  setForm({ ...form, lng: v ? parseFloat(v) : null })
                }
                testid="form-part-lng"
              />
            </div>
            <div className="mt-6">
              <Btn type="submit" size="lg" data-testid="form-part-submit">
                Créer le partenaire
              </Btn>
            </div>
          </Card>
        </form>
      )}

      {items.length === 0 && !show ? (
        <EmptyState
          icon={<Store className="w-6 h-6" />}
          title="Aucun partenaire pour le moment."
          testId="part-empty"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              className="bg-noir-50/50 border border-or/15 hover:border-or/40 rounded-sm p-5 transition"
              data-testid={`part-card-${p.id}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-3xl leading-none">{p.emoji || "🏪"}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-parchemin leading-tight">
                    {p.nom}
                  </div>
                  <div className="text-xs font-mono text-parchemin/50 mt-1">
                    {p.categorie} ·{" "}
                    <span className="text-or">{p.valeurMaxEur}€/pers</span>
                  </div>
                  <div className="text-sm text-parchemin/75 mt-3 italic font-display">
                    « {p.offre} »
                  </div>
                  {p.adresse && (
                    <div className="text-xs text-parchemin/40 mt-2 font-mono">
                      {p.adresse}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center mt-5 pt-4 border-t border-or/10">
                <span className="text-xs font-mono text-parchemin/50 tracking-wider">
                  {p._count?.bons || 0} bons générés
                </span>
                <Btn
                  variant="dangerText"
                  size="sm"
                  onClick={() => remove(p.id)}
                  data-testid={`part-del-${p.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                  Supprimer
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
